import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { EvaOsMark } from "@/components/landing/EvaOsMark";
import { SiteFooter } from "@/components/site/SiteFooter";

export const cream = "#F8F4EC";
export const ink = "#1a1a1a";
export const muted = "#6b6357";
export const gold = "#a08c6e";
export const amber = "#C4823A";
export const line = "rgba(0,0,0,0.08)";

export default function AmbassadorShell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  const { user, signOut } = useAuth();
  return (
    <div style={{ minHeight: "100vh", background: cream, color: ink, fontFamily: "Barlow, sans-serif", lineHeight: 1.6 }}>
      <style>{`
        @keyframes evaFadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes evaPulseDot { 0%,100% { opacity: 1; } 50% { opacity: 0.55; } }
        .eva-amb-card-hover { transition: transform 200ms ease, box-shadow 200ms ease; }
        .eva-amb-card-hover:hover { transform: translateY(-2px); box-shadow: 0 14px 40px -22px rgba(0,0,0,0.25); }
        @media (prefers-reduced-motion: reduce) {
          .eva-amb-card-hover:hover { transform: none; box-shadow: none; }
        }
        @media (max-width: 480px) {
          .eva-amb-nav-secondary { display: none !important; }
        }
      `}</style>
      <header style={{ borderBottom: `1px solid ${line}`, padding: "20px 24px" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 8, textDecoration: "none", color: "inherit", flexShrink: 0 }}>
            <span><EvaOsMark /></span>
          </Link>
          <nav style={{ display: "flex", alignItems: "center", gap: "clamp(10px, 3vw, 20px)", fontSize: 13, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Link to="/ambassadors" style={navLink}>Program</Link>
            <Link to="/ambassadors/playbook" style={navLink}>Playbook</Link>
            <Link to="/ambassadors/terms" className="eva-amb-nav-secondary" style={navLink}>Terms</Link>
            {user ? (
              <>
                <Link to="/ambassadors/dashboard" style={navLink}>Dashboard</Link>
                <Link to="/ambassadors/circle" style={navLink}>Circle</Link>
                <button onClick={signOut} className="eva-amb-nav-secondary" style={{ ...navLink, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Sign out</button>
              </>
            ) : (
              <>
                <Link to="/ambassadors/apply" style={{ ...navLink, color: ink, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: amber, boxShadow: `0 0 8px ${amber}` }} />
                  Apply
                </Link>
                <Link to="/auth?next=/ambassadors/dashboard" style={{ ...navLink, color: ink, fontWeight: 500 }}>Sign in</Link>
              </>
            )}
          </nav>
        </div>
      </header>
      <main style={{ maxWidth: wide ? 1200 : 880, margin: "0 auto", padding: "60px 24px 120px" }}>
        {children}
      </main>
      <div style={{ borderTop: `1px solid ${line}`, padding: "28px 24px", color: muted, fontSize: 12, textAlign: "center" }}>
        eva insiders is subject to the program{" "}
        <Link to="/ambassadors/terms" style={{ color: ink, textDecoration: "underline", textUnderlineOffset: 3 }}>terms</Link>.
      </div>
      <SiteFooter callSource="ambassadors_footer" />
    </div>
  );
}

const navLink: React.CSSProperties = {
  color: muted,
  textDecoration: "none",
  fontFamily: "Barlow, sans-serif",
};

export const h1Style: React.CSSProperties = {
  fontFamily: "'Instrument Serif', serif",
  fontStyle: "italic",
  fontSize: "clamp(48px, 7vw, 84px)",
  lineHeight: 1,
  margin: "0 0 24px",
  fontWeight: 400,
};

export const eyebrow: React.CSSProperties = {
  fontSize: 12,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: amber,
  marginBottom: 12,
};

// Eyebrow with a small amber dot — matches homepage section eyebrow rhythm.
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p style={{ ...eyebrow, display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: amber, boxShadow: `0 0 8px ${amber}` }} />
      {children}
    </p>
  );
}

// Tiny in-house fade-up — no new deps. Uses CSS keyframes via inline style.
export function Reveal({ children, delay = 0, as: As = "div" }: { children: ReactNode; delay?: number; as?: "div" | "section" }) {
  return (
    <As style={{ animation: `evaFadeUp 600ms ease-out ${delay}ms both` }}>{children}</As>
  );
}

export const card: React.CSSProperties = {
  background: "#fff",
  border: `1px solid ${line}`,
  borderRadius: 14,
  padding: 24,
};

export const primaryBtn: React.CSSProperties = {
  display: "inline-block",
  padding: "14px 22px",
  background: ink,
  color: cream,
  borderRadius: 10,
  textDecoration: "none",
  fontWeight: 600,
  border: "none",
  cursor: "pointer",
  fontSize: 15,
  fontFamily: "inherit",
};

export const ghostBtn: React.CSSProperties = {
  display: "inline-block",
  padding: "14px 22px",
  background: "transparent",
  color: ink,
  border: `1px solid ${ink}`,
  borderRadius: 10,
  textDecoration: "none",
  fontWeight: 600,
  cursor: "pointer",
  fontSize: 15,
  fontFamily: "inherit",
};
